/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { __ } from '@web-stories-wp/i18n';
import { Fragment } from '@web-stories-wp/react';
import { Icons, THEME_CONSTANTS } from '@web-stories-wp/design-system';
import {
  generatePatternStyles,
  getOpaquePattern,
  hasGradient,
  hasOpacity,
  PatternPropType,
} from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { Button, BUTTON_VARIANTS, BUTTON_TYPES, BUTTON_SIZES } from '../button';
import { Tooltip } from '../tooltip';
import { PLACEMENT } from '../popup';
import { Text } from '../typography/text';
import { themeHelpers } from '../../theme';

const RADIUS_LARGE = 28;
const RADIUS_SMALL = 24;
const ICON_SIZE = 32;

const Transparent = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  border-radius: 100%;
  ${themeHelpers.transparentBg}
`;

const SwatchButton = styled.button.attrs({ type: 'button' })`
  cursor: pointer;
  background-color: transparent;
  border-color: transparent;
  padding: 0;
  border-width: 0;
  display: block;
  width: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  min-width: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  height: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  min-height: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  border-radius: 100%;
  overflow: hidden;
  position: relative;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      cursor: default;
    `}
  ${({ theme }) => themeHelpers.focusableOutlineCSS(theme.colors.border.focus)};

  ::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.divider.primary};
  }
`;

const SwatchPreview = styled(SwatchButton).attrs({ as: 'div', type: '' })``;

const presetCSS = css`
  display: block;
  width: 100%;
  height: 100%;
  font-size: 13px;
  position: relative;

  svg {
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
    position: absolute;
    top: calc(50% - ${ICON_SIZE / 2}px);
    left: calc(50% - ${ICON_SIZE / 2}px);
    filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.4));
  }
`;
const SwatchItem = styled.div.attrs(({ $pattern }) => ({
  style: generatePatternStyles($pattern),
}))`
  ${presetCSS}
  transform: rotate(${({ displaySplit }) => (displaySplit ? -45 : 0)}deg);

  svg {
    color: ${({ theme }) => theme.colors.fg.primary};
    transform: rotate(${({ displaySplit }) => (displaySplit ? 45 : 0)}deg);
  }
`;

const OpaqueColorWrapper = styled.div`
  height: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  width: 50%;
  overflow: hidden;
  position: absolute;
  left: 0;
  top: 0;
`;

const OpaqueColor = styled.div.attrs(({ pattern }) => ({
  style: generatePatternStyles(pattern),
}))`
  height: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  width: ${({ isSmall }) => (isSmall ? RADIUS_SMALL : RADIUS_LARGE)}px;
  position: absolute;
  top: 0;
  left: 0;
`;

const SWATCH_ACTION_GAP = 1;

const SwatchActions = styled.div`
  display: none;
  background: ${({ theme }) => theme.colors.bg.secondary};
  position: absolute;
  top: 0;
  right: 4px;
  height: 100%;
  gap: ${SWATCH_ACTION_GAP}px;
`;

const SWATCH_ACTION_SIZE = 24;

const SwatchAction = styled(Button).attrs({
  size: BUTTON_SIZES.SMALL,
  variant: BUTTON_VARIANTS.SQUARE,
})`
  width: ${SWATCH_ACTION_SIZE}px;
  height: ${SWATCH_ACTION_SIZE}px;
  justify-content: center;

  ${({ iconSize }) =>
    iconSize &&
    css`
      svg {
        width: ${iconSize}px;
        height: ${iconSize}px;
        min-width: ${iconSize}px;
      }
    `}

  color: ${({ theme }) => theme.colors.interactiveBg.primaryPress};

  &:hover {
    color: ${({ theme }) => theme.colors.fg.primary};
  }
`;

const InlineSwatchPreview = styled.button.attrs({ type: 'button' })`
  position: relative;
  cursor: pointer;
  background-color: transparent;
  border-color: transparent;
  padding: 0;
  border-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &:focus-visible {
    outline: none;
  }

  &:hover {
    ${({ actionsWidth }) =>
      css`
        padding-right: ${actionsWidth}px;
      `}

    ${SwatchActions} {
      display: flex;
    }
  }
`;

const SwatchLabel = styled(Text)`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding-right: (--actions-width, 0px);

  ${({ hasTitle, theme }) =>
    !hasTitle &&
    css`
      color: ${theme.colors.fg.tertiary};
    `}
`;

function Swatch({
  onEdit,
  onError,
  onDelete,
  pattern,
  isDisabled = false,
  isSmall = false,
  children,
  isPreview,
  className = '',
  title,
  showTitle,
  ...props
}) {
  if (!pattern) {
    return null;
  }
  const swatchHasTransparency = hasOpacity(pattern);
  const swatchIsGradient = hasGradient(pattern);
  const opaquePattern = swatchHasTransparency
    ? getOpaquePattern(pattern)
    : pattern;
  // Small swatches and gradient swatches are never split.
  const displaySplit = !isSmall && !swatchIsGradient && swatchHasTransparency;
  const SwatchDisplay = isPreview ? SwatchPreview : InlineSwatchPreview;
  const SwatchInnerDisplay = isPreview ? Fragment : SwatchPreview;
  const totalActions = [onEdit, onError, onDelete].filter(Boolean).length;
  const showActions = !isPreview && totalActions > 0;

  return (
    <SwatchDisplay
      actionsWidth={
        totalActions * SWATCH_ACTION_SIZE +
        (totalActions - 1) * SWATCH_ACTION_GAP +
        4
      }
      disabled={isDisabled}
      isSmall={isSmall}
      className={isPreview ? className : undefined}
      {...props}
    >
      <SwatchInnerDisplay
        disabled={isDisabled}
        isSmall={isSmall}
        className={isPreview ? undefined : className}
        {...props}
      >
        {swatchHasTransparency && <Transparent />}
        <SwatchItem
          $pattern={pattern}
          disabled={isDisabled}
          displaySplit={displaySplit}
        >
          {displaySplit && (
            <OpaqueColorWrapper isSmall={isSmall}>
              <OpaqueColor isSmall={isSmall} pattern={opaquePattern} />
            </OpaqueColorWrapper>
          )}
          {children}
        </SwatchItem>
      </SwatchInnerDisplay>
      {showTitle && !isPreview && (
        <SwatchLabel
          size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
          hasTitle={!!title}
        >
          {title || 'Untitled'}
        </SwatchLabel>
      )}
      {showActions && (
        <SwatchActions>
          {onError && (
            <Tooltip
              placement={PLACEMENT.TOP}
              title={__('Retry', 'web-stories')}
              hasTail
            >
              <SwatchAction
                aria-label={__('Retry', 'web-stories')}
                iconSize={24}
                onClick={(event) => {
                  event.stopPropagation();
                  onError();
                }}
              >
                <Icons.ExclamationTriangle />
              </SwatchAction>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip
              placement={PLACEMENT.TOP}
              title={__('Edit Color', 'web-stories')}
              hasTail
            >
              <SwatchAction
                aria-label={__('Edit Color', 'web-stories')}
                iconSize={24}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
              >
                <Icons.Edit />
              </SwatchAction>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip
              placement={PLACEMENT.TOP}
              title={__('Delete Color', 'web-stories')}
              hasTail
            >
              <SwatchAction
                aria-label={__('Delete Color', 'web-stories')}
                iconSize={32}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
              >
                <Icons.Trash />
              </SwatchAction>
            </Tooltip>
          )}
        </SwatchActions>
      )}
    </SwatchDisplay>
  );
}

Swatch.propTypes = {
  onEdit: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func,
  children: PropTypes.node,
  pattern: PatternPropType,
  isDisabled: PropTypes.bool,
  isSmall: PropTypes.bool,
  isPreview: PropTypes.bool,
  className: PropTypes.string,
  showTitle: PropTypes.bool,
};

export { Swatch };
